<template>
  <div>
    <b-card-code>
      <!-- Modal for adding/editing work day -->
      <b-modal
        id="workDayModal"
        :title="editMode ? 'Nap szerkesztése' : 'Új nap hozzáadása'"
        hide-footer
        size="lg"
      >
        <b-row class="my-1 pl-1 pr-1">
          <b-col lg="12">
            <b-form-group
              label="Dátum"
              label-for="date"
            >
              <b-form-datepicker
                id="date"
                v-model="form.date"
                locale="hu"
                :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
              />
            </b-form-group>
          </b-col>
          <b-col lg="12">
            <b-form-group
              label="Típus"
              label-for="type"
            >
              <b-form-select
                id="type"
                v-model="form.type"
                :options="dayTypes"
                value-field="value"
                text-field="label"
              >
                <template #first>
                  <b-form-select-option
                    :value="''"
                    disabled
                  >
                    Válassz típust
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col lg="12">
            <b-form-group
              label="Megjegyzés"
              label-for="description"
            >
              <b-form-textarea
                id="description"
                v-model="form.description"
                rows="3"
                placeholder="Opcionális megjegyzés"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="12"
            class="justify-content-end d-flex my-2"
          >
            <b-button
              size="sm"
              variant="danger"
              @click="closeModal()"
            >
              Mégsem
            </b-button>
            <b-button
              v-if="editMode"
              class="ml-1"
              size="sm"
              variant="warning"
              @click="deleteWorkDay()"
            >
              Törlés
            </b-button>
            <b-button
              class="ml-1"
              size="sm"
              variant="success"
              @click="saveWorkDay()"
            >
              Mentés
            </b-button>
          </b-col>
        </b-row>
      </b-modal>

      <!-- Year selector and header -->
      <b-row class="mb-0">
        <b-col md="12">
          <b-card>
            <div class="d-flex justify-content-between align-items-center">
              <h2 class="mb-0">
                Munkaszüneti és áthelyezett napok kezelése
              </h2>
              <div class="d-flex align-items-center">
                <h1 class="mb-0">
                  <span
                    class="cursor-pointer"
                    @click="year--"
                  >
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="32"
                    />
                  </span>
                  <span class="mx-3">{{ year }}</span>
                  <span
                    class="cursor-pointer"
                    @click="year++"
                  >
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="32"
                    />
                  </span>
                </h1>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Legend -->
      <b-row class="mb-0">
        <b-col md="12">
          <b-card>
            <div class="d-flex flex-wrap justify-content-center gap-3">
              <div class="d-flex align-items-center mx-3">
                <div class="legend-box holiday" />
                <span class="ml-2">Munkaszüneti nap</span>
              </div>
              <div class="d-flex align-items-center mx-3">
                <div class="legend-box work-day" />
                <span class="ml-2">Áthelyezett munkanap</span>
              </div>
              <div class="d-flex align-items-center mx-3">
                <div class="legend-box rest-day" />
                <span class="ml-2">Áthelyezett pihenőnap</span>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Calendar view -->
      <b-row>
        <b-col
          v-for="month in months"
          :key="month.number"
          md="4"
          class="mb-3"
        >
          <b-card class="month-card">
            <h4 class="text-center mb-3 month-title">
              {{ month.name }}
            </h4>
            <div class="calendar-grid">
              <!-- Day headers -->
              <div
                v-for="dayName in dayNames"
                :key="dayName"
                class="calendar-header"
              >
                {{ dayName }}
              </div>
              <!-- Empty cells for days before month starts -->
              <div
                v-for="n in month.startDay"
                :key="'empty-' + n"
                class="calendar-day empty"
              />
              <!-- Days of month -->
              <div
                v-for="day in month.days"
                :key="day.date"
                class="calendar-day"
                :class="getDayClass(day)"
                @click="openDayModal(day)"
              >
                <div class="day-number">
                  {{ day.day }}
                </div>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-card-code>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormDatepicker,
  BFormSelect,
  BFormSelectOption,
  BModal,
} from 'bootstrap-vue'
import BCardCode from '@core/components/b-card-code'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import axios from 'axios'

export default {
  components: {
    BCardCode,
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    // eslint-disable-next-line vue/no-unused-components
    BFormInput,
    BFormTextarea,
    BFormDatepicker,
    BFormSelect,
    BFormSelectOption,
    BModal,
    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  data() {
    return {
      year: new Date().getFullYear(),
      months: [],
      dayNames: ['H', 'K', 'Sz', 'Cs', 'P', 'Sz', 'V'],
      dayTypes: [
        { value: 'holiday', label: 'Munkaszüneti nap' },
        { value: 'work_day', label: 'Áthelyezett munkanap' },
        { value: 'rest_day', label: 'Áthelyezett pihenőnap' },
      ],
      workDays: [],
      editMode: false,
      form: {
        id: null,
        date: '',
        type: '',
        description: '',
      },
    }
  },
  watch: {
    year() {
      this.initializeCalendar()
      this.loadWorkDays()
    },
  },
  mounted() {
    this.initializeCalendar()
    this.loadWorkDays()
  },
  methods: {
    initializeCalendar() {
      const monthNames = [
        'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
        'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December',
      ]

      this.months = monthNames.map((name, index) => {
        const month = index
        const firstDay = new Date(this.year, month, 1)
        const lastDay = new Date(this.year, month + 1, 0)
        const startDay = (firstDay.getDay() + 6) % 7 // Convert Sunday=0 to Monday=0

        const days = []
        // eslint-disable-next-line no-plusplus
        for (let day = 1; day <= lastDay.getDate(); day++) {
          const date = new Date(this.year, month, day)
          const dateString = this.formatDate(date)
          const dayOfWeek = date.getDay()
          days.push({
            day,
            date: dateString,
            isSaturday: dayOfWeek === 6,
            isSunday: dayOfWeek === 0,
            isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
            workDayInfo: null,
          })
        }

        return {
          number: month + 1,
          name,
          days,
          startDay,
        }
      })
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    loadWorkDays() {
      // TODO: Replace with actual API endpoint
      axios({
        method: 'get',
        url: `workdays/list/${this.year}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
        .then(response => {
          // Normalize dates from backend
          this.workDays = response.data.map(wd => ({
            ...wd,
            date: wd.date.split('T')[0], // Extract only the date part (YYYY-MM-DD)
          }))
          this.updateCalendarWithWorkDays()
        })
        .catch(() => {
          // If API doesn't exist yet, use empty array
          this.workDays = []
          this.updateCalendarWithWorkDays()
        })
    },
    updateCalendarWithWorkDays() {
      this.months.forEach(month => {
        // eslint-disable-next-line no-param-reassign
        month.days.forEach(day => {
          const workDay = this.workDays.find(wd => wd.date === day.date)
          if (workDay) {
            // eslint-disable-next-line no-param-reassign
            day.workDayInfo = workDay
          } else {
            // eslint-disable-next-line no-param-reassign
            day.workDayInfo = null
          }
        })
      })
    },
    getDayClass(day) {
      const classes = []
      // Speciális napok prioritást kapnak
      if (day.workDayInfo) {
        classes.push(day.workDayInfo.type)
      } else if (day.isSaturday) {
        classes.push('saturday')
      } else if (day.isSunday) {
        classes.push('sunday')
      }
      classes.push('clickable')
      return classes.join(' ')
    },
    getDayTypeLabel(type) {
      const dayType = this.dayTypes.find(dt => dt.value === type)
      return dayType ? dayType.label : ''
    },
    openDayModal(day) {
      if (day.workDayInfo) {
        // Edit mode
        this.editMode = true
        this.form = {
          id: day.workDayInfo.id,
          date: day.date,
          type: day.workDayInfo.type,
          description: day.workDayInfo.description || '',
        }
      } else {
        // Create mode
        this.editMode = false
        this.form = {
          id: null,
          date: day.date,
          type: '',
          description: '',
        }
      }
      this.$bvModal.show('workDayModal')
    },
    closeModal() {
      this.$bvModal.hide('workDayModal')
      this.resetForm()
    },
    resetForm() {
      this.form = {
        id: null,
        date: '',
        type: '',
        description: '',
      }
      this.editMode = false
    },
    saveWorkDay() {
      if (!this.form.date || !this.form.type) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Hiba',
            text: 'Kérlek töltsd ki a kötelező mezőket!',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }

      const method = this.editMode ? 'put' : 'post'
      const url = this.editMode
        ? `workdays/update/${this.form.id}`
        : 'workdays/create'

      axios({
        method,
        url,
        data: this.form,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sikeres mentés',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.closeModal()
          this.loadWorkDays()
        })
        .catch(error => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Hiba',
              text: error.response?.data?.message || 'Hiba történt a mentés során',
              icon: 'XOctagonIcon',
              variant: 'danger',
            },
          })
        })
    },
    deleteWorkDay() {
      if (!this.form.id) return

      this.$swal({
        title: 'Biztosan törlöd?',
        text: 'Ez a művelet nem visszavonható!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Igen, törlöm!',
        cancelButtonText: 'Mégsem',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          axios({
            method: 'delete',
            url: `workdays/delete/${this.form.id}`,
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          })
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Sikeres törlés',
                  icon: 'CheckIcon',
                  variant: 'success',
                },
              })
              this.closeModal()
              this.loadWorkDays()
            })
            .catch(error => {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Hiba',
                  text: error.response?.data?.message || 'Hiba történt a törlés során',
                  icon: 'XOctagonIcon',
                  variant: 'danger',
                },
              })
            })
        }
      })
    },
  },
}
</script>

<style scoped lang="scss">
.cursor-pointer {
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
}

.legend-box {
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 2px solid #ddd;
}

.legend-box.holiday {
  background-color: #ea5455;
}

.legend-box.work-day {
  background-color: #28c76f;
}

.legend-box.rest-day {
  background-color: #00cfe8;
}

.legend-box.saturday {
  background-color: #ff9f43;
}

.legend-box.sunday {
  background-color: #ea5455;
}

.month-card {
  background: #161d31;
  border: none;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.4);
  }

  .card-body {
    padding: 1.5rem;
    overflow: hidden;
  }
}

.month-title {
  color: #7367f0;
  font-weight: 700;
  font-size: 1.25rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #3b4253;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin: 0;
  padding: 0;
}

.calendar-header {
  text-align: center;
  font-weight: 600;
  padding: 8px 2px;
  background: #283046;
  color: #b4b7bd;
  border-radius: 6px;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.calendar-day {
  aspect-ratio: 1;
  border: 2px solid #3b4253;
  border-radius: 6px;
  padding: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 55px;
  position: relative;
  transition: all 0.3s ease;
  background: #283046;
  color: #b4b7bd;
  overflow: hidden;

  &.empty {
    border: none;
    background: transparent;
  }

  &.clickable {
    cursor: pointer;
    &:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 8px 16px rgba(115, 103, 240, 0.3);
      border-color: #7367f0;
      background: #2f3749;
    }
  }

  &.saturday {
    background: #3b4253;
    border-color: #4b5563;
    color: #ff9f43;
    font-weight: 600;

    &:hover {
      background: #4b5563;
      border-color: #ff9f43;
    }
  }

  &.sunday {
    background: #3b4253;
    border-color: #4b5563;
    color: #ea5455;
    font-weight: 600;

    &:hover {
      background: #4b5563;
      border-color: #ea5455;
    }
  }

  &.holiday {
    background: linear-gradient(135deg, #ea5455 0%, #c23616 100%);
    color: white;
    font-weight: bold;
    border-color: #ea5455;
    box-shadow: 0 4px 12px rgba(234, 84, 85, 0.4);

    &:hover {
      box-shadow: 0 6px 16px rgba(234, 84, 85, 0.5);
    }
  }

  &.work_day {
    background: linear-gradient(135deg, #28c76f 0%, #1e8449 100%);
    color: white;
    font-weight: bold;
    border-color: #28c76f;
    box-shadow: 0 4px 12px rgba(40, 199, 111, 0.4);

    &:hover {
      box-shadow: 0 6px 16px rgba(40, 199, 111, 0.5);
    }
  }

  &.rest_day {
    background: linear-gradient(135deg, #00cfe8 0%, #0097a7 100%);
    color: white;
    font-weight: bold;
    border-color: #00cfe8;
    box-shadow: 0 4px 12px rgba(0, 207, 232, 0.4);

    &:hover {
      box-shadow: 0 6px 16px rgba(0, 207, 232, 0.5);
    }
  }

  .day-number {
    font-size: 1rem;
    font-weight: 700;
    margin-bottom: 2px;
    line-height: 1;
  }

  .day-info {
    font-size: 0.55rem;
    text-align: center;
    line-height: 1.1;
    margin-top: 2px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    opacity: 0.95;
    word-break: break-word;
    max-width: 100%;
  }
}

// Tablet nézet
@media (max-width: 992px) {
  .month-card .card-body {
    padding: 1rem;
  }

  .month-title {
    font-size: 1.1rem;
  }

  .calendar-grid {
    gap: 3px;
  }

  .calendar-header {
    padding: 6px 2px;
    font-size: 0.65rem;
  }

  .calendar-day {
    min-height: 50px;
    padding: 3px;
    border-width: 1.5px;
    border-radius: 4px;

    .day-number {
      font-size: 0.9rem;
    }

    .day-info {
      font-size: 0.5rem;
      margin-top: 1px;
    }

    &:hover {
      transform: translateY(-2px) scale(1.01);
    }
  }
}

// Mobil nézet
@media (max-width: 768px) {
  .month-card .card-body {
    padding: 0.75rem;
  }

  .month-title {
    font-size: 1rem;
    padding-bottom: 0.25rem;
  }

  .calendar-grid {
    gap: 2px;
  }

  .calendar-header {
    padding: 5px 1px;
    font-size: 0.6rem;
    letter-spacing: 0.2px;
  }

  .calendar-day {
    min-height: 42px;
    padding: 2px;
    border-width: 1px;
    border-radius: 3px;

    .day-number {
      font-size: 0.8rem;
      margin-bottom: 1px;
    }

    .day-info {
      font-size: 0.45rem;
      letter-spacing: 0.1px;
      margin-top: 1px;
    }

    &:hover {
      transform: translateY(-1px);
    }
  }
}

// Kis mobil nézet
@media (max-width: 576px) {
  .month-card .card-body {
    padding: 0.5rem;
  }

  .month-title {
    font-size: 0.9rem;
  }

  .calendar-grid {
    gap: 2px;
  }

  .calendar-header {
    padding: 4px 0;
    font-size: 0.55rem;
  }

  .calendar-day {
    min-height: 38px;
    padding: 1px;

    .day-number {
      font-size: 0.75rem;
    }

    .day-info {
      font-size: 0.4rem;
      display: none; // Elrejtjük a típus címkét kis mobilon
    }

    &:hover {
      transform: none;
      box-shadow: 0 2px 4px rgba(115, 103, 240, 0.3);
    }
  }

  // Kis mobilon egyszerűbb hover
  .calendar-day.clickable:hover {
    transform: none;
    scale: 1;
  }
}
</style>
